import { useMixpanelTrackingFunction } from "@workleap/telemetry/react";
import { useCallback } from "react";
import type { ComparisonDimension } from "#shared/comparisonDimension.ts";
import type { PeriodSelection } from "#shared/periodSelection.ts";
import type { CalibrationDimension, NineBoxCellId } from "#shared/reviewScoresContract.ts";
import type { TimeFrame } from "@domains/engagement/shared/timeFrame.ts";
import type { CUSTOM_FEEDBACK_PERIOD } from "./feedbackPeriod.ts";
import type { ScoreBucketGranularity } from "@domains/reviews/shared/scoreBuckets.ts";
import { describePeriod } from "../components/comparison/comparisonSeries.ts";

const EventNames = {
    PerformanceAnalyticsViewed: "Performance Analytics Viewed",
    PerformanceAnalyticsAnalyzed: "Performance Analytics Analyzed",
    AnalyticsEmptyStateShown: "Analytics Empty State Shown",
    ButtonClicked: "ButtonClicked"
} as const;

const Trigger = {
    Exported: "Analytics Exported",
    Shared: "Analytics Shared",
    FilterApplied: "Analytics Filter Applied",
    Grouped: "Analytics Grouped",
    BrokenDown: "Analytics Broken Down",
    ComparisonAdded: "Analytics Comparison Added",
    Drilldown: "Analytics Drilldown",
    CalibrationRowExpanded: "Analytics Calibration Row Expanded",
    ChartModeChanged: "Analytics Chart Mode Changed",
    FiltersCleared: "Analytics Filters Cleared",
    TimeRangeApplied: "Analytics Time Range Applied"
} as const;

const PropertyNames = {
    Tab: "Analytics Tab",
    IsDefaultSelection: "Analytics Is Default Selection",
    RevieweeCount: "Analytics Reviewee Count",
    RatedCount: "Analytics Rated Count",
    ExportFormat: "Analytics Export Format",
    ShareMethod: "Analytics Share Method",
    PeriodMode: "Analytics Period Mode",
    PeriodType: "Analytics Period Type",
    CycleCount: "Analytics Cycle Count",
    FilterCount: "Analytics Filter Count",
    FilterDimensions: "Analytics Filter Dimensions",
    FilterDimension: "Analytics Filter Dimension",
    GroupBy: "Analytics Group By",
    BreakdownDimension: "Analytics Breakdown Dimension",
    ComparisonCount: "Analytics Comparison Count",
    ChartMode: "Analytics Chart Mode",
    DrilldownSource: "Analytics Drilldown Source",
    NineBoxCell: "Analytics NineBox Cell",
    ScoreBuckets: "Analytics Score Buckets",
    SeriesCount: "Analytics Series Count",
    TurnIndex: "Analytics Turn Index",
    DurationMs: "Duration Ms",
    Succeeded: "Succeeded",
    EmptyStateReason: "Analytics Empty State Reason",
    TimeRange: "Analytics Time Range"
} as const;

export const AnalyticsTabs = {
    Scores: "Scores",
    Calibration: "Calibration",
    Themes: "Themes",
    FeedbackOverview: "Feedback Overview"
} as const;

export type AnalyticsTab = (typeof AnalyticsTabs)[keyof typeof AnalyticsTabs];

export const AnalyticsExportFormats = {
    PDF: "PDF",
    CSV: "CSV"
} as const;

export type AnalyticsExportFormat = (typeof AnalyticsExportFormats)[keyof typeof AnalyticsExportFormats];

export const AnalyticsShareMethods = {
    CopyLink: "CopyLink",
    Email: "Email"
} as const;

export type AnalyticsShareMethod = (typeof AnalyticsShareMethods)[keyof typeof AnalyticsShareMethods];

const AnalyticsPeriodModes = {
    Cycles: "Cycles",
    TimeRange: "TimeRange"
} as const;

type AnalyticsPeriodMode = (typeof AnalyticsPeriodModes)[keyof typeof AnalyticsPeriodModes];

const AnalyticsPeriodTypes = {
    AllCycles: "AllCycles",
    SpecificCycles: "SpecificCycles",
    LastReview: "LastReview",
    Past90Days: "Past90Days",
    LastYear: "LastYear",
    Last2Years: "Last2Years",
    Custom: "Custom"
} as const;

type AnalyticsPeriodType = (typeof AnalyticsPeriodTypes)[keyof typeof AnalyticsPeriodTypes];

export const AnalyticsFilterDimensions = {
    DirectManagers: "DirectManagers",
    ReportingLines: "ReportingLines",
    Segment: "Segment",
    Department: "Department",
    Gender: "Gender",
    WorkLocation: "WorkLocation",
    RoleLevel: "RoleLevel",
    JobTitle: "JobTitle",
    Tenure: "Tenure",
    Track: "Track",
    FeedbackType: "FeedbackType",
    Sentiment: "Sentiment",
    Metric: "Metric"
} as const;

export type AnalyticsFilterDimension = (typeof AnalyticsFilterDimensions)[keyof typeof AnalyticsFilterDimensions];

const AnalyticsBreakdownDimensions = {
    ...AnalyticsFilterDimensions,
    ReviewCycle: "ReviewCycle",
    TimeRange: "TimeRange",
    None: "None"
} as const;

export type AnalyticsBreakdownDimension = (typeof AnalyticsBreakdownDimensions)[keyof typeof AnalyticsBreakdownDimensions];

const AnalyticsGroupBys = {
    DirectManager: "DirectManager",
    Segment: "Segment",
    Department: "Department",
    JobTitle: "JobTitle",
    Tenure: "Tenure",
    None: "None"
} as const;

export type AnalyticsGroupBy = (typeof AnalyticsGroupBys)[keyof typeof AnalyticsGroupBys];

const AnalyticsChartModes = {
    Distribution: "Distribution",
    NineBox: "NineBox"
} as const;

export type AnalyticsChartMode = (typeof AnalyticsChartModes)[keyof typeof AnalyticsChartModes];

const AnalyticsScoreBuckets = {
    Broad: "Broad",
    Medium: "Medium",
    Detailed: "Detailed"
} as const;

export type AnalyticsScoreBucket = (typeof AnalyticsScoreBuckets)[keyof typeof AnalyticsScoreBuckets];

export const AnalyticsEmptyStateReasons = {
    NoData: "NoData",
    FilteredOut: "FilteredOut"
} as const;

export type AnalyticsEmptyStateReason = (typeof AnalyticsEmptyStateReasons)[keyof typeof AnalyticsEmptyStateReasons];

export const AnalyticsDrilldownSources = {
    DistributionBar: "DistributionBar",
    NineBoxCell: "NineBoxCell"
} as const;

const AnalyticsNineBoxCells = {
    Enigma: "Enigma",
    Growth: "Growth",
    Stars: "Stars",
    Dilemma: "Dilemma",
    Core: "Core",
    HighImpact: "HighImpact",
    Risk: "Risk",
    Effective: "Effective",
    Trusted: "Trusted"
} as const;

export type AnalyticsNineBoxCell = (typeof AnalyticsNineBoxCells)[keyof typeof AnalyticsNineBoxCells];

const NINE_BOX_CELL_BY_ID: Record<NineBoxCellId, AnalyticsNineBoxCell> = {
    enigma: AnalyticsNineBoxCells.Enigma,
    growth: AnalyticsNineBoxCells.Growth,
    stars: AnalyticsNineBoxCells.Stars,
    dilemma: AnalyticsNineBoxCells.Dilemma,
    core: AnalyticsNineBoxCells.Core,
    "high-impact": AnalyticsNineBoxCells.HighImpact,
    risk: AnalyticsNineBoxCells.Risk,
    effective: AnalyticsNineBoxCells.Effective,
    trusted: AnalyticsNineBoxCells.Trusted
};

export type AnalyticsDrilldown = { source: typeof AnalyticsDrilldownSources.DistributionBar } | { source: typeof AnalyticsDrilldownSources.NineBoxCell; cell: AnalyticsNineBoxCell };

type AnalyticsFilterInputs = {
    managerIds: string[];
    reportingLineIds: string[];
    segmentIds: string[];
    departments: string[];
    genders: string[];
    workLocations: string[];
    jobLevels: string[];
    jobTitles: string[];
    tenure: string | null;
    track: string | null;
};

const FILTER_DIMENSION_ENTRIES = [
    ["managerIds", AnalyticsFilterDimensions.DirectManagers],
    ["reportingLineIds", AnalyticsFilterDimensions.ReportingLines],
    ["segmentIds", AnalyticsFilterDimensions.Segment],
    ["departments", AnalyticsFilterDimensions.Department],
    ["genders", AnalyticsFilterDimensions.Gender],
    ["workLocations", AnalyticsFilterDimensions.WorkLocation],
    ["jobLevels", AnalyticsFilterDimensions.RoleLevel],
    ["jobTitles", AnalyticsFilterDimensions.JobTitle],
    ["tenure", AnalyticsFilterDimensions.Tenure],
    ["track", AnalyticsFilterDimensions.Track]
] as const satisfies readonly (readonly [keyof AnalyticsFilterInputs, AnalyticsFilterDimension])[];

const BREAKDOWN_DIMENSION_BY_COMPARISON: Record<ComparisonDimension, AnalyticsBreakdownDimension> = {
    cycle: AnalyticsBreakdownDimensions.ReviewCycle,
    timeRange: AnalyticsBreakdownDimensions.TimeRange,
    manager: AnalyticsBreakdownDimensions.DirectManagers,
    reportingLine: AnalyticsBreakdownDimensions.ReportingLines,
    segment: AnalyticsBreakdownDimensions.Segment,
    function: AnalyticsBreakdownDimensions.Department,
    gender: AnalyticsBreakdownDimensions.Gender,
    workLocation: AnalyticsBreakdownDimensions.WorkLocation,
    roleLevel: AnalyticsBreakdownDimensions.RoleLevel,
    jobTitle: AnalyticsBreakdownDimensions.JobTitle,
    tenure: AnalyticsBreakdownDimensions.Tenure,
    track: AnalyticsBreakdownDimensions.Track
};

const GROUP_BY_BY_CALIBRATION: Record<CalibrationDimension, AnalyticsGroupBy> = {
    manager: AnalyticsGroupBys.DirectManager,
    segment: AnalyticsGroupBys.Segment,
    function: AnalyticsGroupBys.Department,
    jobTitle: AnalyticsGroupBys.JobTitle,
    tenure: AnalyticsGroupBys.Tenure
};

const SCORE_BUCKET_BY_GRANULARITY: Record<ScoreBucketGranularity, AnalyticsScoreBucket> = {
    whole: AnalyticsScoreBuckets.Broad,
    half: AnalyticsScoreBuckets.Medium,
    quarter: AnalyticsScoreBuckets.Detailed
};

function isActiveFilterValue(value: string[] | string | null): boolean {
    return Array.isArray(value) ? value.length > 0 : !!value;
}

export function toAnalyticsFilterDimensions(filters: AnalyticsFilterInputs): AnalyticsFilterDimension[] {
    return FILTER_DIMENSION_ENTRIES.flatMap(([key, dimension]) => (isActiveFilterValue(filters[key]) ? [dimension] : []));
}

export function toAnalyticsBreakdownDimension(dimension: ComparisonDimension | undefined): AnalyticsBreakdownDimension {
    return dimension ? BREAKDOWN_DIMENSION_BY_COMPARISON[dimension] : AnalyticsBreakdownDimensions.None;
}

export function toAnalyticsGroupBy(dimension: CalibrationDimension): AnalyticsGroupBy {
    return GROUP_BY_BY_CALIBRATION[dimension];
}

export function toAnalyticsScoreBucket(granularity: ScoreBucketGranularity): AnalyticsScoreBucket {
    return SCORE_BUCKET_BY_GRANULARITY[granularity];
}

export function toAnalyticsChartMode(isNineBoxView: boolean): AnalyticsChartMode {
    return isNineBoxView ? AnalyticsChartModes.NineBox : AnalyticsChartModes.Distribution;
}

export function toAnalyticsNineBoxCell(cellId: NineBoxCellId): AnalyticsNineBoxCell {
    return NINE_BOX_CELL_BY_ID[cellId];
}

export interface AnalyticsPeriodProperties {
    mode: AnalyticsPeriodMode;
    type: AnalyticsPeriodType;
    cycleCount: number;
}

export function describeAnalyticsPeriod(period: PeriodSelection): AnalyticsPeriodProperties {
    const descriptor = describePeriod(period);

    switch (descriptor.kind) {
        case "all":
            return { mode: AnalyticsPeriodModes.Cycles, type: AnalyticsPeriodTypes.AllCycles, cycleCount: 0 };
        case "cycles":
            return { mode: AnalyticsPeriodModes.Cycles, type: AnalyticsPeriodTypes.SpecificCycles, cycleCount: descriptor.count };
        case "range":
            return { mode: AnalyticsPeriodModes.TimeRange, type: AnalyticsPeriodTypes.Custom, cycleCount: 0 };
        case "preset":
            return { mode: AnalyticsPeriodModes.TimeRange, type: PERIOD_TYPE_BY_PRESET[descriptor.preset], cycleCount: 0 };
    }
}

const PERIOD_TYPE_BY_PRESET = {
    LastReview: AnalyticsPeriodTypes.LastReview,
    PastNinetyDays: AnalyticsPeriodTypes.Past90Days,
    LastYear: AnalyticsPeriodTypes.LastYear,
    LastTwoYears: AnalyticsPeriodTypes.Last2Years
} as const;

export interface PerformanceAnalyticsViewedProperties {
    tab: AnalyticsTab;
    isDefaultSelection: boolean;
    period: AnalyticsPeriodProperties;
    filterDimensions: AnalyticsFilterDimension[];
    breakdownDimension: AnalyticsBreakdownDimension;
    comparisonCount: number;
    revieweeCount?: number;
    ratedCount?: number;
    chartMode?: AnalyticsChartMode;
    scoreBuckets?: AnalyticsScoreBucket;
    groupBy?: AnalyticsGroupBy;
}

export interface PerformanceAnalyticsAnalyzedProperties {
    tab: AnalyticsTab;
    seriesCount: number;
    turnIndex: number;
    durationMs: number;
    succeeded: boolean;
}

type AnalyticsTimeRange = TimeFrame | typeof CUSTOM_FEEDBACK_PERIOD;

type AnalyticsEvent = readonly [name: string, properties: Record<string, unknown>];

export function performanceAnalyticsViewedEvent({ tab, isDefaultSelection, period, filterDimensions, breakdownDimension, comparisonCount, revieweeCount, ratedCount, chartMode, scoreBuckets, groupBy }: PerformanceAnalyticsViewedProperties): AnalyticsEvent {
    return [
        EventNames.PerformanceAnalyticsViewed,
        {
            [PropertyNames.Tab]: tab,
            [PropertyNames.IsDefaultSelection]: isDefaultSelection,
            [PropertyNames.PeriodMode]: period.mode,
            [PropertyNames.PeriodType]: period.type,
            [PropertyNames.CycleCount]: period.cycleCount,
            [PropertyNames.FilterCount]: filterDimensions.length,
            [PropertyNames.FilterDimensions]: filterDimensions,
            [PropertyNames.BreakdownDimension]: breakdownDimension,
            [PropertyNames.ComparisonCount]: comparisonCount,
            ...(revieweeCount !== undefined ? { [PropertyNames.RevieweeCount]: revieweeCount } : {}),
            ...(ratedCount !== undefined ? { [PropertyNames.RatedCount]: ratedCount } : {}),
            ...(chartMode !== undefined ? { [PropertyNames.ChartMode]: chartMode } : {}),
            ...(scoreBuckets !== undefined ? { [PropertyNames.ScoreBuckets]: scoreBuckets } : {}),
            ...(groupBy !== undefined ? { [PropertyNames.GroupBy]: groupBy } : {})
        }
    ];
}

export function analyticsExportedEvent(format: AnalyticsExportFormat, tab: AnalyticsTab): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.Exported, [PropertyNames.ExportFormat]: format, [PropertyNames.Tab]: tab }];
}

export function analyticsSharedEvent(method: AnalyticsShareMethod, tab: AnalyticsTab): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.Shared, [PropertyNames.ShareMethod]: method, [PropertyNames.Tab]: tab }];
}

export function analyticsFilterAppliedEvent(dimension: AnalyticsFilterDimension): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.FilterApplied, [PropertyNames.FilterDimension]: dimension }];
}

export function analyticsGroupedEvent(groupBy: AnalyticsGroupBy): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.Grouped, [PropertyNames.GroupBy]: groupBy }];
}

export function analyticsBrokenDownEvent(dimension: AnalyticsBreakdownDimension): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.BrokenDown, [PropertyNames.BreakdownDimension]: dimension }];
}

export function analyticsComparisonAddedEvent(comparisonCount: number): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.ComparisonAdded, [PropertyNames.ComparisonCount]: comparisonCount }];
}

export function analyticsDrilldownEvent(drilldown: AnalyticsDrilldown): AnalyticsEvent {
    return [
        EventNames.ButtonClicked,
        {
            Trigger: Trigger.Drilldown,
            [PropertyNames.DrilldownSource]: drilldown.source,
            ...(drilldown.source === AnalyticsDrilldownSources.NineBoxCell ? { [PropertyNames.NineBoxCell]: drilldown.cell } : {})
        }
    ];
}

export function analyticsCalibrationRowExpandedEvent(groupBy: AnalyticsGroupBy): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.CalibrationRowExpanded, [PropertyNames.GroupBy]: groupBy }];
}

export function analyticsChartModeChangedEvent(chartMode: AnalyticsChartMode): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.ChartModeChanged, [PropertyNames.ChartMode]: chartMode }];
}

export function analyticsFiltersClearedEvent(): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.FiltersCleared }];
}

export function analyticsTimeRangeAppliedEvent(range: AnalyticsTimeRange): AnalyticsEvent {
    return [EventNames.ButtonClicked, { Trigger: Trigger.TimeRangeApplied, [PropertyNames.TimeRange]: range }];
}

export function performanceAnalyticsAnalyzedEvent({ tab, seriesCount, turnIndex, durationMs, succeeded }: PerformanceAnalyticsAnalyzedProperties): AnalyticsEvent {
    return [
        EventNames.PerformanceAnalyticsAnalyzed,
        {
            [PropertyNames.Tab]: tab,
            [PropertyNames.SeriesCount]: seriesCount,
            [PropertyNames.TurnIndex]: turnIndex,
            [PropertyNames.DurationMs]: durationMs,
            [PropertyNames.Succeeded]: succeeded
        }
    ];
}

export function analyticsEmptyStateShownEvent(reason: AnalyticsEmptyStateReason, tab: AnalyticsTab): AnalyticsEvent {
    return [EventNames.AnalyticsEmptyStateShown, { [PropertyNames.Tab]: tab, [PropertyNames.EmptyStateReason]: reason }];
}

export function useAnalyticsTracking() {
    const track = useMixpanelTrackingFunction();

    const trackPerformanceAnalyticsViewed = useCallback((properties: PerformanceAnalyticsViewedProperties) => void track(...performanceAnalyticsViewedEvent(properties)), [track]);
    const trackExported = useCallback((format: AnalyticsExportFormat, tab: AnalyticsTab) => void track(...analyticsExportedEvent(format, tab)), [track]);
    const trackShared = useCallback((method: AnalyticsShareMethod, tab: AnalyticsTab) => void track(...analyticsSharedEvent(method, tab)), [track]);
    const trackFilterApplied = useCallback((dimension: AnalyticsFilterDimension) => void track(...analyticsFilterAppliedEvent(dimension)), [track]);
    const trackGrouped = useCallback((groupBy: AnalyticsGroupBy) => void track(...analyticsGroupedEvent(groupBy)), [track]);
    const trackBrokenDown = useCallback((dimension: AnalyticsBreakdownDimension) => void track(...analyticsBrokenDownEvent(dimension)), [track]);
    const trackComparisonAdded = useCallback((comparisonCount: number) => void track(...analyticsComparisonAddedEvent(comparisonCount)), [track]);
    const trackDrilldown = useCallback((drilldown: AnalyticsDrilldown) => void track(...analyticsDrilldownEvent(drilldown)), [track]);
    const trackCalibrationRowExpanded = useCallback((groupBy: AnalyticsGroupBy) => void track(...analyticsCalibrationRowExpandedEvent(groupBy)), [track]);
    const trackChartModeChanged = useCallback((chartMode: AnalyticsChartMode) => void track(...analyticsChartModeChangedEvent(chartMode)), [track]);
    const trackFiltersCleared = useCallback(() => void track(...analyticsFiltersClearedEvent()), [track]);
    const trackTimeRangeApplied = useCallback((range: AnalyticsTimeRange) => void track(...analyticsTimeRangeAppliedEvent(range)), [track]);
    const trackPerformanceAnalyticsAnalyzed = useCallback((properties: PerformanceAnalyticsAnalyzedProperties) => void track(...performanceAnalyticsAnalyzedEvent(properties)), [track]);
    const trackEmptyStateShown = useCallback((reason: AnalyticsEmptyStateReason, tab: AnalyticsTab) => void track(...analyticsEmptyStateShownEvent(reason, tab)), [track]);

    return {
        trackPerformanceAnalyticsViewed,
        trackExported,
        trackShared,
        trackFilterApplied,
        trackGrouped,
        trackBrokenDown,
        trackComparisonAdded,
        trackDrilldown,
        trackCalibrationRowExpanded,
        trackChartModeChanged,
        trackFiltersCleared,
        trackTimeRangeApplied,
        trackPerformanceAnalyticsAnalyzed,
        trackEmptyStateShown
    };
}
